import { ClipboardList, Send } from "lucide-react";
import {
  PageHeading, Section, Field, Steps, NextStep, Callout, RoleBadge,
  Diagram, MockField, MockSelect, MockButton, MockTable, MockRow,
} from "../shared";

export default function FormsMeetingTasksSection() {
  return (
    <div data-testid="guide-forms-meeting-tasks">
      <PageHeading
        icon={ClipboardList}
        title="Log Meeting Tasks"
        description="Record a meeting and the tasks that came out of it, then turn those tasks into real tracked action items in one click."
      />

      <div className="flex items-center gap-2 mb-4">
        <RoleBadge role="admin" />
        <span className="text-xs text-white/40">General Admin and above.</span>
      </div>

      <Callout variant="info" title="In plain words">
        This is your <span className="text-white">meeting minutes form</span>. You note who attended and what was decided, list the to-dos the meeting produced, and then press one button to create all those to-dos as proper tracked tasks.
      </Callout>

      <Section title="When do I use this?">
        <ul className="list-disc list-inside text-sm text-white/65 space-y-1.5 ml-2">
          <li>After a Board, ExCom, IC, or similar meeting, to capture the minutes and action items together.</li>
          <li>When you want all the meeting's tasks registered at once, instead of typing them in one by one.</li>
        </ul>
      </Section>

      <Section title="What the form looks like">
        <Diagram
          frameLabel="Meeting Tasks → New Meeting Tasks"
          legend={[
            <>Give the meeting a short <span className="text-white">name</span>.</>,
            <>Pick the meeting <span className="text-white">type</span> (Board, ExCom, IC…).</>,
            <>Add the <span className="text-white">action items</span> the meeting produced — one row each.</>,
            <>Save, then click <span className="text-white">Register Tasks</span> to turn them into real tracked tasks.</>,
          ]}
        >
          <MockRow>
            <MockField label="Meeting name" value="Q2 Board Meeting" marker={1} />
            <MockSelect label="Type" value="Board" marker={2} />
          </MockRow>
          <div className="flex items-center gap-1.5 mb-1">
            <span className="text-2xs uppercase tracking-wide text-white/45">Action items</span>
          </div>
          <MockTable
            columns={["Title", "Lane", "Assigned", "Due"]}
            rows={[
              ["Draft FY27 budget", "SDF MD", "Amal R.", "30 Jul"],
              ["Portfolio risk review", "Board / ExCom", "Sara K.", "15 Aug"],
            ]}
          />
          <MockButton label="Register Tasks" marker={4} />
          <p className="text-2xs text-white/40">The Action items table above is marker <span className="text-cyan-300">③</span>.</p>
        </Diagram>
      </Section>

      <Section title="Step-by-step">
        <Steps
          steps={[
            <>Open <span className="text-white">Meeting Tasks</span> from the header and click <span className="text-white">New Meeting Tasks</span>, then fill in the meeting <span className="text-white">name</span>.</>,
            <>Pick the meeting <span className="text-white">type</span> and fill in the rest of the meeting details.</>,
            <>In the <span className="text-white">Action items</span> section, add one row for each task the meeting produced.</>,
            <>Save the entry, then click the green <span className="text-white">Register Tasks</span> button to create real tracked tasks.</>,
          ]}
        />
      </Section>

      <Section title="Meeting details — boxes explained">
        <Field name="Meeting Name" required="required">
          <p>A short, human-friendly label (e.g. "Q2 Board Meeting", "Investment Committee", "Audit Review"). It flows through to every task created from this entry and appears on every email about those tasks.</p>
        </Field>

        <Field name="Reference Number" required="optional">
          <p>The formal meeting reference if you have one (e.g. "BOR-2026-Q2").</p>
        </Field>

        <Field name="Type" required="required">
          <p>Board, ExCom, IC, etc. The list comes from the Reference Lists set up by your Super Admin.</p>
        </Field>

        <Field name="Date / Time / Location / Chair" required="optional">
          <p>Standard meeting details. Useful for the record but not blocking.</p>
        </Field>

        <Field name="Logged By" required="required">
          <p>The person who prepared these minutes (dropdown of active user accounts). May or may not be you.</p>
        </Field>

        <Field name="Sent By" required="auto">
          <p>Captured automatically from your login at submit time. Cannot be edited. Appears in registration emails when it differs from Logged By.</p>
        </Field>

        <Field name="Quorum confirmed" required="optional">
          <p>A toggle confirming the meeting had enough attendees for decisions to be valid.</p>
        </Field>
      </Section>

      <Section title="Repeatable sections — boxes explained">
        <Field name="Attendance" required="optional">
          <p>Add as many attendees as needed — each with name, role/function, and present/absent. Click the + button to add rows.</p>
        </Field>

        <Field name="Agenda items" required="optional">
          <p>Numbered agenda. For each: topic, presenter, discussion summary, decision (Approved/Deferred/Rejected/Noted), and any conditions.</p>
        </Field>

        <Field name="Action items" required="required">
          <p>The tasks the meeting produced — this is the most important part of the form. Each row needs:</p>
          <ul className="list-disc list-inside ml-3 space-y-0.5">
            <li>Title (short summary)</li>
            <li>Authority lane</li>
            <li>Assigned person (from team members)</li>
            <li>Due date</li>
            <li>Priority</li>
            <li>Sign-off required toggle</li>
            <li>Instruction (the brief)</li>
          </ul>
          <p className="text-white/40">These turn one-to-one into tracked action items when you register.</p>
        </Field>

        <Field name="Key decisions register" required="optional">
          <p>Formal decisions made during the meeting. Separate from agenda discussion — for the decisions you want to surface clearly.</p>
        </Field>

        <Field name="Next meeting" required="optional">
          <p>Date and topic for the follow-up.</p>
        </Field>

        <Field name="Prepared by / Approved by" required="optional">
          <p>Sign-off names for the minutes themselves.</p>
        </Field>
      </Section>

      <NextStep>
        <p>After saving:</p>
        <ul className="list-disc list-inside space-y-1 ml-2">
          <li>The entry is saved as <span className="text-white">Draft</span> (or Finalized if you set it).</li>
          <li>It appears in the Meeting Tasks list with the date and a status badge.</li>
          <li>A green <span className="inline-flex items-center gap-1"><Send className="w-3 h-3 text-emerald-300" /> Register Tasks</span> button appears. Clicking it creates real tracked action items from each row in the Action Items section — setting the origin type, linking the meeting reference, and copying title / instruction / assignee / due / priority / lane.</li>
          <li>Each registered item links back to this entry — you can trace them on the Origins page.</li>
          <li>Emails go out to each new item's assignee, showing both Logged By and Sent By (where they differ).</li>
        </ul>
      </NextStep>

      <Callout variant="info" title="Draft vs Finalized">
        Save as Draft while preparing. Switch to Finalized once approved. Both show in the list with colour-coded badges. You can register tasks from a Draft — there's no need to finalize first.
      </Callout>
    </div>
  );
}
